import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, User, AlertCircle, WifiOff, ShieldCheck, QrCode, CheckCircle, Clock, RefreshCw, Megaphone, Gift, Users } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { staffApi } from '../../services/api';
import { buildPublicUrl } from '../../services/origin';
import NumPad from '../components/NumPad';
import OfflineBanner from '../../components/OfflineBanner';
import { enqueueOfflineAction } from '../../utils/offlineQueue';
import { playBip } from '../../utils/bip';

export default function CustomerRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    name: '', phone: location.state?.phone || '',
    birthday: '', email: '', campaign_id: '', acquisition_channel: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPhonePad, setShowPhonePad] = useState(!location.state?.phone);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [campaignPrize, setCampaignPrize] = useState(null);

  const [step, setStep] = useState('form'); // 'form' | 'qr' | 'success'
  const [pendingCustomer, setPendingCustomer] = useState(null);
  const [consentUrl, setConsentUrl] = useState('');
  const [successData, setSuccessData] = useState(null);
  const pollingRef = useRef(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Il nome è obbligatorio';
    if (!form.phone || form.phone.length < 9) e.phone = 'Inserisci un numero valido (min 9 cifre)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Load active campaigns
  useEffect(() => {
    staffApi.get('/campaigns/active')
      .then(r => { setActiveCampaigns(r.data.campaigns || []); })
      .catch(err => { console.error('[CAMPAIGNS] Fetch error:', err); });
  }, []);

  // Step 1: Submit form → create pending customer → show QR
  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    const body = {
      name: form.name.trim(),
      phone: form.phone,
      birthday: form.birthday || null,
      email: form.email || null,
      campaign_id: form.campaign_id || null,
      acquisition_channel: form.acquisition_channel || null,
    };
    try {
      const res = await staffApi.post('/customers', body);
      setPendingCustomer(res.data);
      // Derive the consent URL from the CURRENT page origin so the QR
      // always points to the same domain the staff is browsing — host-
      // agnostic, follows the user across deploys/custom domains.
      setConsentUrl(buildPublicUrl(`/consent/${res.data.consent_token}`));
      setStep('qr');
      toast.success('QR code generato! Fallo scansionare al cliente.');
      startPolling(res.data.id);
    } catch (err) {
      if (!navigator.onLine) {
        await enqueueOfflineAction({
          url: '/api/customers',
          method: 'POST',
          body,
          label: `Registrazione ${form.name.trim()}`,
        });
        setSuccessData({ offline: true, customer: { name: form.name.trim() }, welcome_points: '?' });
        setStep('success');
        toast.info('Registrazione salvata in coda offline.');
      } else {
        toast.error(err.response?.data?.detail || 'Errore durante la registrazione');
      }
    } finally {
      setLoading(false);
    }
  };

  // Poll for consent status
  const startPolling = (customerId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await staffApi.get(`/customers/${customerId}/consent-status`);
        if (res.data.status === 'active' && res.data.gdpr_consent) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          // Audio cue so the cashier hears the consent landing even
          // without looking at the screen.
          playBip();
          // Fetch updated customer data
          const custRes = await staffApi.get(`/customers/${customerId}`);
          // If campaign was selected, assign prize
          let assignedPrize = null;
          if (form.campaign_id) {
            try {
              const prizeRes = await staffApi.post(`/campaigns/${form.campaign_id}/assign-prize`, { customer_id: customerId });
              assignedPrize = prizeRes.data;
              setCampaignPrize(assignedPrize);
            } catch (err) {
              console.error('Prize assignment error:', err);
            }
          }
          setSuccessData({
            customer: custRes.data,
            welcome_points: custRes.data.points || 50,
            campaignPrize: assignedPrize,
          });
          setStep('success');
          toast.success(assignedPrize
            ? `Registrazione completata! Premio assegnato: ${assignedPrize.prize_name}`
            : 'Consenso ricevuto! Cliente registrato con successo!'
          );
          setTimeout(() => navigate(`/staff/customer/${customerId}`), 5000);
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);
  };

  // Regenerate QR (extend token expiry)
  const handleRegenerate = async () => {
    if (!pendingCustomer) return;
    setLoading(true);
    try {
      const res = await staffApi.post('/customers', {
        name: form.name.trim(),
        phone: form.phone,
        birthday: form.birthday || null,
        email: form.email || null,
      });
      setPendingCustomer(res.data);
      setConsentUrl(buildPublicUrl(`/consent/${res.data.consent_token}`));
      startPolling(res.data.id);
      toast.success('Nuovo QR code generato!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella rigenerazione');
    } finally {
      setLoading(false);
    }
  };

  // ─── SUCCESS SCREEN ─────────────────────────────────────────────────────────
  if (step === 'success') {
    const isOffline = successData?.offline;
    const prize = successData?.campaignPrize;
    return (
      <div className="staff-app flex flex-col items-center justify-center min-h-dvh p-6" data-testid="register-success">
        <div className="text-center">
          <div className={`w-24 h-24 ${isOffline ? 'bg-[#FFAA00]/20 border-2 border-[#FFAA00]' : 'bg-[#3ECF8E]/20 border-2 border-[#3ECF8E]'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {isOffline ? <WifiOff size={48} className="text-[#FFAA00]" /> : <CheckCircle size={48} className="text-[#3ECF8E]" />}
          </div>
          <h2 className="text-white font-bold text-2xl mb-2">
            {isOffline ? 'Salvato in coda offline!' : 'Registrazione completata!'}
          </h2>
          <p className="text-[#888888] text-base mb-4">
            {isOffline
              ? `${successData.customer.name} verrà registrato quando torni online`
              : `${successData.customer.name} è ora nel programma fedeltà`}
          </p>
          {!isOffline && (
            <div className="bg-[#18181E] border-[#3ECF8E]/30 rounded-2xl p-5 border inline-block mb-4">
              <div className="text-[#3ECF8E] text-5xl font-bold tabular-nums">+{successData.welcome_points}</div>
              <div className="text-[#888888] text-sm mt-1">punti benvenuto</div>
            </div>
          )}
          {prize && (
            <div className="bg-[#18181E] border-[#D44E20]/30 rounded-2xl p-5 border mt-4" data-testid="campaign-prize-assigned">
              <Gift size={32} className="text-[#D44E20] mx-auto mb-2" />
              <div className="text-[#D44E20] text-lg font-bold">{prize.prize_name}</div>
              <div className="text-[#888888] text-sm mt-1">Premio campagna assegnato!</div>
              <div className="text-[#555] text-xs mt-2">Il cliente potrà scoprirlo scansionando il QR del volantino</div>
            </div>
          )}
          <p className="text-[#555] text-sm mt-6">
            {isOffline ? 'Ritorno alla home...' : 'Reindirizzamento alla scheda cliente...'}
          </p>
        </div>
      </div>
    );
  }

  // ─── QR CODE SCREEN (waiting for consent) ───────────────────────────────────
  if (step === 'qr') {
    return (
      <div className="staff-app min-h-dvh" data-testid="qr-consent-screen">
        <div className="px-4 pt-6 pb-4 flex items-center gap-3">
          <button
            onClick={() => {
              if (pollingRef.current) clearInterval(pollingRef.current);
              setStep('form');
            }}
            data-testid="qr-back-btn"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-[#18181E] text-[#888] hover:text-white transition-colors border border-white/5"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-white font-bold text-xl">Consenso Privacy</h1>
        </div>

        <div className="px-4 pb-8">
          {/* Customer info card */}
          <div className="bg-[#18181E] rounded-2xl p-5 border border-white/5 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-[#D44E20]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={24} className="text-[#D44E20]" />
              </div>
              <div>
                <p className="text-white font-semibold text-base">{pendingCustomer?.name}</p>
                <p className="text-[#888] text-sm">{pendingCustomer?.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[#FFAA00] text-sm">
              <Clock size={14} />
              <span>In attesa del consenso privacy...</span>
            </div>
          </div>

          {/* QR Code */}
          <div className="bg-white rounded-2xl p-6 mb-6 flex flex-col items-center" data-testid="qr-code-container">
            <QRCodeSVG
              value={consentUrl}
              size={220}
              level="M"
              includeMargin={false}
            />
            <p className="text-gray-600 text-sm mt-4 text-center font-medium">
              Fai scansionare questo QR al cliente
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5 mb-6">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-[#3ECF8E] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium mb-1">Come funziona</p>
                <ol className="text-[#888] text-xs leading-relaxed space-y-1 list-decimal list-inside">
                  <li>Il cliente scansiona il QR code col telefono</li>
                  <li>Si apre la pagina con l'informativa privacy</li>
                  <li>Il cliente legge e accetta</li>
                  <li>La registrazione si completa automaticamente</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Polling indicator */}
          <div className="flex items-center justify-center gap-2 text-[#888] text-sm mb-4">
            <div className="w-2 h-2 bg-[#3ECF8E] rounded-full animate-pulse" />
            <span>Monitoraggio in corso...</span>
          </div>

          {/* Regenerate button */}
          <button
            onClick={handleRegenerate}
            disabled={loading}
            data-testid="regenerate-qr-btn"
            className="w-full mt-2 text-[#888] text-sm text-center flex items-center justify-center gap-2 hover:text-white transition-colors disabled:opacity-40 py-3"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Generazione...' : 'Rigenera QR code'}
          </button>
        </div>
      </div>
    );
  }

  // ─── REGISTRATION FORM (Step 1) ─────────────────────────────────────────────
  return (
    <div className="staff-app min-h-dvh" data-testid="register-form-screen">
      <OfflineBanner token={localStorage.getItem('staff_token')} />
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="w-10 h-10 flex items-center justify-center rounded-full bg-[#18181E] text-[#888] hover:text-white transition-colors border border-white/5">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-white font-bold text-xl">Nuovo Cliente</h1>
      </div>

      <div className="px-4 pb-8 space-y-4">
        {/* Name */}
        <div>
          <label className="text-[#888888] text-sm font-medium block mb-1.5">Nome completo *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Mario Rossi"
            data-testid="input-name"
            className="w-full h-12 bg-[#222228] text-white placeholder:text-[#444] rounded-[14px] px-4 text-base border border-transparent focus:border-[#D44E20] focus:outline-none transition-colors"
          />
          {errors.name && <p className="text-[#FF5555] text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.name}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="text-[#888888] text-sm font-medium block mb-1.5">Numero di telefono *</label>
          <div
            className={`w-full h-12 bg-[#222228] text-white rounded-[14px] px-4 text-base border flex items-center cursor-pointer transition-colors ${showPhonePad ? 'border-[#D44E20]' : 'border-transparent'}`}
            onClick={() => setShowPhonePad(true)}
            data-testid="phone-display-register"
          >
            <span className={form.phone ? 'text-white' : 'text-[#444]'}>
              {form.phone || 'Tocca per inserire'}
            </span>
          </div>
          {errors.phone && <p className="text-[#FF5555] text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} />{errors.phone}</p>}
          {showPhonePad && (
            <div className="mt-3">
              <NumPad value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} maxLength={10} />
              <button onClick={() => setShowPhonePad(false)} className="mt-3 w-full text-[#888] text-sm text-center">Chiudi tastierino</button>
            </div>
          )}
        </div>

        {/* Birthday (optional) */}
        <div>
          <label className="text-[#888888] text-sm font-medium block mb-1.5">Data di nascita <span className="text-[#555]">(opzionale)</span></label>
          <input
            type="date"
            value={form.birthday}
            onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))}
            data-testid="input-birthday"
            className="w-full h-12 bg-[#222228] text-white rounded-[14px] px-4 text-base border border-transparent focus:border-[#D44E20] focus:outline-none transition-colors"
          />
        </div>

        {/* Email (optional) */}
        <div>
          <label className="text-[#888888] text-sm font-medium block mb-1.5">Email <span className="text-[#555]">(opzionale)</span></label>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="mario@email.it"
            data-testid="input-email"
            className="w-full h-12 bg-[#222228] text-white placeholder:text-[#444] rounded-[14px] px-4 text-base border border-transparent focus:border-[#D44E20] focus:outline-none transition-colors"
          />
        </div>

        {/* QR info */}
        <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5">
          <div className="flex items-start gap-3">
            <QrCode size={20} className="text-[#3ECF8E] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-medium mb-1">Registrazione con QR Code</p>
              <p className="text-[#888] text-xs leading-relaxed">
                Verrà generato un QR code che il cliente dovrà scansionare per accettare la privacy policy e completare l'iscrizione.
              </p>
            </div>
          </div>
        </div>

        {/* Campaign selector — always visible */}
        <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5">
          <div className="flex items-start gap-3">
            <Megaphone size={20} className="text-[#D44E20] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium mb-2">Campagna di acquisizione</p>
              <p className="text-[#888] text-xs mb-3">Il cliente è arrivato tramite una campagna specifica?</p>
              {activeCampaigns.length > 0 ? (
                <select
                  value={form.campaign_id}
                  onChange={e => setForm(f => ({ ...f, campaign_id: e.target.value }))}
                  data-testid="campaign-select"
                  className="w-full h-11 bg-[#222228] text-white rounded-[14px] px-4 text-sm border border-transparent focus:border-[#D44E20] focus:outline-none appearance-none"
                >
                  <option value="">Nessuna campagna</option>
                  {activeCampaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-[#555] text-xs italic">Nessuna campagna attiva al momento</p>
              )}
            </div>
          </div>
        </div>

        {/* Acquisition channel */}
        <div className="bg-[#18181E] rounded-2xl p-4 border border-white/5">
          <div className="flex items-start gap-3">
            <Users size={20} className="text-[#5B8DEF] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium mb-2">Come ci ha conosciuto?</p>
              <select
                value={form.acquisition_channel}
                onChange={e => setForm(f => ({ ...f, acquisition_channel: e.target.value }))}
                data-testid="acquisition-channel-select"
                className="w-full h-11 bg-[#222228] text-white rounded-[14px] px-4 text-sm border border-transparent focus:border-[#5B8DEF] focus:outline-none appearance-none"
              >
                <option value="">Non specificato</option>
                <option value="passaparola">Passaparola</option>
                <option value="social">Social media</option>
                <option value="volantino">Volantinaggio</option>
                <option value="cartellone">Cartelloni / Affissioni</option>
                <option value="radio">Radio</option>
                <option value="stampa">Stampa / Giornali</option>
                <option value="evento">Evento</option>
                <option value="google">Google / Ricerca online</option>
                <option value="passaggio">Passaggio casuale</option>
                <option value="altro">Altro</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full h-14 bg-[#D44E20] text-white font-bold text-lg rounded-[14px] disabled:opacity-50 active:scale-95 transition-all mt-2 flex items-center justify-center gap-2 shadow-lg shadow-orange-900/30"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : <QrCode size={20} />}
          {loading ? 'Creazione...' : 'Genera QR Code'}
        </button>
      </div>
    </div>
  );
}
